import { useQuery } from '@tanstack/react-query';
import type { Capability, SetupStatusDTO } from '@bifeai/shared';
import { useToast } from '@bifeai/ui';
import { ChevronDown, ChevronUp, Circle, CircleCheck, Rocket, X } from 'lucide-react';
import { useState } from 'react';
import { Link, useLocation } from 'react-router';
import { api } from '../lib/api';
import { setHelp, useHelp } from '../lib/help';
import { useSession } from '../lib/session';

interface Step {
  key: string;
  label: string;
  done: boolean;
  to: string;
  action: string;
  optional?: boolean;
}

/** Arma la lista de pasos según el plan, los adicionales y los permisos de quien la ve. */
export function useSetupSteps(): { steps: Step[]; done: number; total: number; loading: boolean } {
  const { me, can, isFranchiseUser } = useSession();
  const q = useQuery({
    queryKey: ['setup'],
    queryFn: () => api.get<SetupStatusDTO>('/dashboard/setup'),
    staleTime: 60_000,
  });
  const s = q.data;
  const mods = me.tenant.modules;
  const steps: (Step & { cap?: Capability; when?: boolean })[] = [
    mods.includes('MULTI_BRANCH')
      ? { key: 'branches', label: 'Sucursales cargadas', done: (s?.branches ?? 0) > 1, to: '/sucursales', action: 'Cargar', cap: 'branches.manage' }
      : { key: 'branch', label: 'Datos de la sucursal', done: !!s?.hqHasAddress, to: '/sucursales', action: 'Completar', cap: 'branches.manage' },
    { key: 'team', label: 'Equipo con su usuario', done: (s?.users ?? 0) > 1, to: '/usuarios', action: 'Sumar', cap: 'users.manage' },
    { key: 'mfa', label: 'Verificación en dos pasos', done: me.user.totpEnabled, to: '/cuenta', action: 'Activar' },
    { key: 'pin', label: 'PIN para el mostrador', done: me.user.pinSet, to: '/cuenta', action: 'Crear' },
    { key: 'products', label: 'Catálogo de productos y precios', done: !!s?.hasProducts, to: '/productos', action: 'Cargar', cap: 'prices.manage' },
    { key: 'suppliers', label: 'Proveedores cargados', done: (s?.suppliers ?? 0) > 0, to: '/proveedores', action: 'Cargar', cap: 'suppliers.manage' },
    { key: 'purchase', label: 'Primera compra cargada', done: !!s?.hasPurchases, to: '/compras/nueva', action: 'Cargar', cap: 'purchases.manage' },
    {
      key: 'scale',
      label: 'Balanza e impresora de tickets',
      done: !!s?.scaleConfigured,
      to: '/configuracion',
      action: 'Configurar',
      cap: 'settings.manage',
    },
    { key: 'sale', label: 'Primera venta en el mostrador', done: !!s?.hasSales, to: '/venta', action: 'Vender', cap: 'pos.sell' },
    { key: 'cash', label: 'Primer cierre de caja', done: !!s?.hasClosedCash, to: '/caja', action: 'Ir a caja', cap: 'cash.own' },
    {
      key: 'cutting',
      label: 'Primera media res despostada',
      done: !!s?.hasCuttingRun,
      to: '/desposte',
      action: 'Despostar',
      cap: 'cutting.manage',
      when: mods.includes('CUTTING'),
    },
    {
      key: 'attendance',
      label: 'Empleados con PIN de asistencia',
      done: (s?.employeesWithPin ?? 0) > 0,
      to: '/empleados',
      action: 'Cargar',
      cap: 'employees.manage',
      when: mods.includes('ATTENDANCE'),
    },
    {
      key: 'arca',
      label: 'Facturación ARCA configurada y probada',
      done: !!s?.fiscalReady,
      to: '/configuracion?tab=fiscal',
      action: 'Configurar',
      cap: 'settings.manage',
      when: mods.includes('ARCA'),
    },
    {
      key: 'mp',
      label: 'Mercado Pago conectado',
      done: !!s?.mpConnected,
      to: '/configuracion?tab=sales',
      action: 'Conectar',
      optional: true,
      cap: 'settings.manage',
    },
    {
      key: 'customers',
      label: 'Clientes con cuenta corriente',
      done: !!s?.hasCustomers,
      to: '/clientes',
      action: 'Cargar',
      optional: true,
      cap: 'pos.sell',
    },
  ];
  if (me.tenant.plan === 'BIFI') {
    // Solo bifi: sin mostrador, compras ni caja. Lo que necesita bifi para vender: locales, equipo y catálogo con precios.
    const keep = new Set(['branches', 'branch', 'team', 'mfa', 'products', 'mp']);
    for (let n = steps.length - 1; n >= 0; n--) if (!keep.has(steps[n]!.key)) steps.splice(n, 1);
  }
  if (isFranchiseUser) {
    // Franquicia: su equipo, sus cuentas (Mercado Pago y ARCA) y su primera venta. Lo de la marca no le toca.
    const keep = new Set(['team', 'mfa', 'pin', 'sale', 'cash', 'customers']);
    for (let n = steps.length - 1; n >= 0; n--) if (!keep.has(steps[n]!.key)) steps.splice(n, 1);
    steps.push(
      {
        key: 'mp',
        label: 'Tu Mercado Pago conectado',
        done: !!s?.mpConnected,
        to: '/configuracion',
        action: 'Conectar',
        optional: true,
        cap: 'franchise.accounts',
      },
      {
        key: 'arca',
        label: 'Tu facturación ARCA configurada y probada',
        done: !!s?.fiscalReady,
        to: '/configuracion',
        action: 'Configurar',
        cap: 'franchise.accounts',
        when: mods.includes('ARCA'),
      },
    );
  }
  // Primero lo que falta; lo hecho queda abajo.
  const visible = steps.filter((x) => x.when !== false && (!x.cap || can(x.cap))).sort((a, b) => Number(a.done) - Number(b.done));
  const required = visible.filter((x) => !x.optional);
  return { steps: visible, done: required.filter((x) => x.done).length, total: required.length, loading: !s };
}

/**
 * Puesta en marcha: panel flotante abajo a la derecha. Se minimiza o se cierra;
 * se vuelve a mostrar desde el menú de usuario (arriba a la derecha).
 */
export function SetupWidget() {
  const { me, can } = useSession();
  const prefs = useHelp(me.user.id);
  const { pathname } = useLocation();
  const toast = useToast();
  const allowed = can('settings.manage') || can('users.manage');
  const { steps, done, total, loading } = useSetupSteps();
  // Abierto solo en el Dashboard; en las otras pantallas queda el botoncito para no tapar el trabajo.
  const [expandedAt, setExpandedAt] = useState<string | null>(null);
  // En el mostrador no estorba el botón de cobrar.
  if (!allowed || loading || prefs.setup === 'hidden' || pathname.startsWith('/venta')) return null;
  const complete = done === total;
  const pct = total ? Math.round((done / total) * 100) : 100;

  const close = () => {
    setHelp(me.user.id, { setup: 'hidden' });
    toast.success('Puesta en marcha oculta', 'La volvés a abrir desde tu menú, arriba a la derecha.');
  };

  const minimized = prefs.setup === 'min' || (pathname !== '/' && expandedAt !== pathname);
  const expand = () => (pathname === '/' ? setHelp(me.user.id, { setup: 'open' }) : setExpandedAt(pathname));
  const collapse = () => (pathname === '/' ? setHelp(me.user.id, { setup: 'min' }) : setExpandedAt(null));

  if (minimized) {
    return (
      <button type="button" className="bf-setup-pill" onClick={expand} data-tour="setup">
        <Rocket size={16} aria-hidden /> Puesta en marcha
        <span className="bf-setup-count">
          {done}/{total}
        </span>
        <ChevronUp size={16} aria-hidden />
      </button>
    );
  }

  return (
    <section className="bf-setup" aria-label="Puesta en marcha" data-tour="setup">
      <header className="bf-setup-head">
        <div>
          <strong>
            <Rocket size={16} aria-hidden /> Puesta en marcha
          </strong>
          <span>{complete ? '¡Todo listo para operar!' : `${done} de ${total} pasos`}</span>
        </div>
        <button type="button" className="bf-icon-btn" aria-label="Minimizar" onClick={collapse}>
          <ChevronDown />
        </button>
        <button type="button" className="bf-icon-btn" aria-label="Cerrar" onClick={close}>
          <X />
        </button>
      </header>
      <div className="bf-setup-bar" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
        <span style={{ width: `${pct}%` }} />
      </div>
      <ul className="bf-setup-list">
        {steps.map((st) => (
          <li key={st.key} data-done={st.done}>
            {st.done ? <CircleCheck size={18} aria-label="Hecho" /> : <Circle size={18} aria-label="Pendiente" />}
            <span>
              {st.label}
              {st.optional && <small> (opcional)</small>}
            </span>
            {!st.done && (
              <Link to={st.to} onClick={() => setExpandedAt(null)}>
                {st.action}
              </Link>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
